/**
 * Composition root of the identity domain: one wired slice per flow, built once at start-up.
 *
 * It is initialized before the app renders and not in the post-login bootstrap, because the routes
 * of the invitation and recovery flows are public and would find an uninitialized composition.
 * Building the session slice costs nothing without a session: the credential source reads the cookie
 * on every call, not when it is constructed.
 *
 * The flow is the key and the credential is derived from it, because two flows can authorize with
 * the same credential and still be two different journeys. Keying by credential made the flow travel
 * inside it, and a recovery would have reported itself as an invitation for the rest of its life.
 *
 * @module @identity/composition
 */

import type { AxiosInstance } from 'axios';

import type { ICredentialSourcePort, ILinkTokenStoragePort } from '@identity/application/ports';
import { IDENTITY_FLOWS } from '@identity/domain/types';
import type { IdentityFlow } from '@identity/domain/types';
import {
  createAuthHttpClient,
  identityLinkApi,
  identityPublicApi,
  identitySessionApi,
} from '@identity/infrastructure/api';
import { linkTokenCookieStorage, sessionCredentialSource } from '@identity/infrastructure/storage';

import { createAccountUseCases } from './factories/createAccountUseCases';
import { createLinkUseCases } from './factories/createLinkUseCases';
import { createMagicLinkConsumer } from './factories/createMagicLinkConsumer';
import { createPublicUseCases } from './factories/createPublicUseCases';
import { FLOW_AUTHORIZERS } from './types';
import type {
  AccountUseCases,
  IdentityAuthorizer,
  IdentityComposition,
  IdentitySlice,
  LinkUseCases,
  MagicLinkConsumer,
  PublicUseCases,
} from './types';

export { FLOW_AUTHORIZERS, IDENTITY_AUTHORIZERS } from './types';
export type {
  AccountUseCases,
  IdentityAuthorizer,
  IdentityComposition,
  IdentitySlice,
  LinkUseCases,
  MagicLinkConsumer,
  PublicUseCases,
} from './types';

/** Where each credential is read from, and which axios instance carries it. */
const AUTHORIZER_SOURCES: Record<IdentityAuthorizer, { credentials: ICredentialSourcePort; api: AxiosInstance }> = {
  link: { credentials: linkTokenCookieStorage, api: identityLinkApi },
  session: { credentials: sessionCredentialSource, api: identitySessionApi },
};

/** Everything built at start-up: the slices, plus the two pieces that belong to no flow. */
type IdentityRoot = {
  slices: IdentityComposition;
  magicLinkConsumer: MagicLinkConsumer;
  publicUseCases: PublicUseCases;
};

const createIdentityRoot = (): IdentityRoot => {
  const createSlice = (flow: IdentityFlow): IdentitySlice => {
    const authorizer = FLOW_AUTHORIZERS[flow];
    const { credentials, api } = AUTHORIZER_SOURCES[authorizer];
    const httpClient = createAuthHttpClient({ httpClientIdentity: api });

    return {
      linkUseCases: createLinkUseCases(httpClient, credentials, flow, authorizer),
      accountUseCases: createAccountUseCases(httpClient, credentials, flow, authorizer),
      credentials,
    };
  };

  // The flow is bound to the slice, so no call site has to remember to pass it.
  const slices = IDENTITY_FLOWS.reduce(
    (built, flow) => ({ ...built, [flow]: createSlice(flow) }),
    {} as IdentityComposition,
  );

  return {
    slices,
    magicLinkConsumer: createMagicLinkConsumer(
      createAuthHttpClient({ httpClientIdentity: identityLinkApi }),
      linkTokenCookieStorage,
    ),
    publicUseCases: createPublicUseCases(createAuthHttpClient({ httpClientIdentity: identityPublicApi })),
  };
};

let root: IdentityRoot | null = null;

/** Initializes the composition. It must run once, before the application renders. */
export const initIdentityComposition = (): void => {
  if (root !== null) {
    return;
  }
  root = createIdentityRoot();
};

const getRoot = (): IdentityRoot => {
  if (root === null) {
    throw new Error(
      '[Identity] Composition not initialized.\n' +
        'Call initIdentityComposition() in src/index.js before rendering <App />.',
    );
  }
  return root;
};

/** The composition singleton. It throws when it is read before being initialized. */
export const getIdentityComposition = (): IdentityComposition => getRoot().slices;

/**
 * Clears the singleton between test cases. Required, not convenient: it is initialized outside the
 * post-login bootstrap, so it survives from one test to the next.
 *
 * @internal
 */
export const resetIdentityComposition = (): void => {
  root = null;
};

/** Magic link use cases for one flow. */
export const getLinkUseCases = (flow: IdentityFlow): LinkUseCases => getIdentityComposition()[flow].linkUseCases;

/** Account use cases for one flow. */
export const getAccountUseCases = (flow: IdentityFlow): AccountUseCases =>
  getIdentityComposition()[flow].accountUseCases;

/** The credential source of one flow. Presentation reads the account id through it. */
export const getCredentials = (flow: IdentityFlow): ICredentialSourcePort => getIdentityComposition()[flow].credentials;

/** The link consume. It is not part of any slice: no flow is known until it answers. */
export const getMagicLinkConsumer = (): MagicLinkConsumer => getRoot().magicLinkConsumer;

/** The use cases that authorize with nothing at all. */
export const getPublicUseCases = (): PublicUseCases => getRoot().publicUseCases;

/**
 * The link session, with its writes. It is kept out of the slices on purpose: a caller holding the
 * session slice must have no way to reach a `clear()`.
 */
export const getLinkTokenStorage = (): ILinkTokenStoragePort => linkTokenCookieStorage;
